import { Link } from 'react-router'

import type { ChatMessage } from '../../lib/assistant'
import { NOTICE_COPY } from '../../lib/assistantCopy'
import { messageKind } from '../../lib/assistantThread'
import { EvidenceCard } from './EvidenceCard'
import { MessageFeedback } from './MessageFeedback'
import { MiniBars } from './MiniBars'
import { ReplyText } from './ReplyText'
import { Trace } from './Trace'

/**
 * One message (agent-1-web W3). Everything a reply SAYS is the server's, with
 * no client sentence about a reading (agent-1-web §1, criterion 5). Its text is
 * read as a safe Markdown subset -- bold, italics, short lists, inline code
 * (owner, 2026-10-12, replacing "no markdown"); still no HTML, no links, no
 * images (`ReplyText`, `lib/assistantMarkdown.ts`). The user's own words stay
 * exactly as typed.
 *
 * - the user's own words, right;
 * - a model reply, left, with what it looked at above it, the server's caveats
 *   under it and its cards below;
 * - fixed wording (a `fallback`), left, DASHED (owner 2026-10-12: no line of
 *   "Fixed wording · ..." under it);
 * - an honest decline (`out_of_scope`, `no_reading`, `reading_unsupported`),
 *   which is fixed wording too, plus ONE next step that is a link, not a claim.
 */
export function Message({
  message,
  live,
  onRetry,
  onReload,
  sessionId,
}: {
  message: ChatMessage
  /** The persisted session this reply belongs to; absent before the first session event. */
  sessionId?: string | null
  /** The turn being streamed now: its pending state is "thinking", not "still being written". */
  live: boolean
  /** A retryable fallback's "Try again": the same words, as a new message. */
  onRetry?: () => void
  onReload?: () => void
}) {
  const kind = messageKind(message)

  if (kind === 'user') {
    return (
      <div className="assistant-msg assistant-msg--user">
        <p className="assistant-msg__text">{message.text}</p>
      </div>
    )
  }

  if (kind === 'thinking') {
    return (
      <div className="assistant-turn" aria-busy={live || undefined}>
        <Trace steps={message.tool_steps} />
        {live ? (
          <div className="assistant-msg assistant-msg--bot assistant-msg--thinking">
            <span className="visually-hidden">The assistant is working on a reply.</span>
            <span className="assistant-skel" aria-hidden="true" />
            <span className="assistant-skel" aria-hidden="true" />
            <span className="assistant-skel" aria-hidden="true" />
          </div>
        ) : (
          <div className="assistant-msg assistant-msg--bot assistant-msg--template">
            <p className="assistant-msg__text">{NOTICE_COPY.pending}</p>
            {onReload && (
              <button type="button" className="btn btn--ghost btn--sm" onClick={onReload}>
                Reload the conversation
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  const template = message.source === 'template'
  return (
    <div className="assistant-turn">
      <Trace steps={message.tool_steps} />
      <div className={`assistant-msg assistant-msg--bot${template ? ' assistant-msg--template' : ''}`}>
        <ReplyText text={message.text ?? ''} />
        {message.caveats.length !== 0 && (
          <ul className="assistant-msg__caveats" aria-label="Caveats">
            {message.caveats.map((caveat) => (
              <li key={caveat}>{caveat}</li>
            ))}
          </ul>
        )}
        {message.cards.map((card) => {
          const chart = card.chart
          const measure = chart && message.cards.find((item) => item.ref === chart.measure_ref)
          return chart && measure ? <MiniBars key={`chart-${card.ref}`} chart={chart} title={measure.title} /> : null
        })}
        {/* Inside the reply, as the canvas draws it: the card is part of what
            the reply says, not a separate message. */}
        {message.cards.map((card) => (
          <EvidenceCard key={card.ref} card={card} />
        ))}
      </div>
      {sessionId && kind !== 'declined' && <MessageFeedback key={`${sessionId}/${message.id}`} sessionId={sessionId} messageId={message.id} />}
      <NextStep message={message} onRetry={onRetry} />
    </div>
  )
}

function NextStep({ message, onRetry }: { message: ChatMessage; onRetry?: () => void }) {
  if (message.retryable && onRetry) {
    return (
      <div className="assistant-chips">
        <button type="button" className="assistant-chip" onClick={onRetry}>
          Try again
        </button>
      </div>
    )
  }
  const link =
    message.reason === 'no_reading'
      ? { to: '/record', label: 'Go to Record' }
      : message.reason === 'out_of_scope'
        ? { to: '/report', label: "Open a card's references" }
        : message.reason === 'reading_unsupported'
          ? { to: '/history', label: 'Open History' }
          : null
  if (!link) return null
  return (
    <div className="assistant-chips">
      <Link className="assistant-chip" to={link.to}>
        {link.label}
      </Link>
    </div>
  )
}
